import {
  AggregateRoot,
  RepositoryGetFunction,
  RepositoryGetFunctionArgs,
  RepositorySaveFunction,
  RepositorySaveFunctionArgs,
} from "@lookiero/messaging";
import { BuildBootstrapFunctionReturn, bootstrap as messagingBootstrap } from "@lookiero/messaging-react";
import { PLAY_GAME } from "../../domain/game/command/playGame";
import { Game, playGameHandler } from "../../domain/game/model/game";
import { GamesGetFunction, GamesSaveFunction } from "../../domain/game/model/games";
import {
  GameByCustomerIdView,
  VIEW_GAME_BY_CUSTOMER_ID,
  viewGameByCustomerIdHandler,
} from "../../projection/game/viewGameByCustomerId";

const MESSAGING_CONTEXT_ID = "LookAndLike";

type NeverWhenEmptyRecord<K extends [Record<string, unknown>]> = K extends [Record<string, never>] ? [never?] : K;
type RepositoryDependencies<
  A extends AggregateRoot,
  GetFunctionArgs extends RepositoryGetFunctionArgs,
  SaveFunctionArgs extends RepositorySaveFunctionArgs,
> = Omit<
  Parameters<RepositoryGetFunction<A, GetFunctionArgs>>[0] & Parameters<RepositorySaveFunction<A, SaveFunctionArgs>>[0],
  keyof RepositoryGetFunctionArgs
>;

interface BaseBootstrapFunctionArgs<
  GameGetFunctionArgs extends RepositoryGetFunctionArgs,
  GameSaveFunctionArgs extends RepositorySaveFunctionArgs,
> {
  readonly gameByCustomerIdView: GameByCustomerIdView;
  readonly getGame: GamesGetFunction<GameGetFunctionArgs>;
  readonly saveGame: GamesSaveFunction<GameSaveFunctionArgs>;
  readonly gamesDependencies: NeverWhenEmptyRecord<
    [RepositoryDependencies<Game, GameGetFunctionArgs, GameSaveFunctionArgs>]
  >;
}

interface BaseBootstrapFunction {
  <GameGetFunctionArgs extends RepositoryGetFunctionArgs, GameSaveFunctionArgs extends RepositorySaveFunctionArgs>(
    args: BaseBootstrapFunctionArgs<GameGetFunctionArgs, GameSaveFunctionArgs>,
  ): BuildBootstrapFunctionReturn;
}

const baseBootstrap: BaseBootstrapFunction = ({ gameByCustomerIdView, getGame, saveGame, gamesDependencies }) =>
  messagingBootstrap({ id: MESSAGING_CONTEXT_ID })
    .query(VIEW_GAME_BY_CUSTOMER_ID, viewGameByCustomerIdHandler, {
      view: gameByCustomerIdView,
    })
    .command(PLAY_GAME, playGameHandler)(getGame, saveGame, ...gamesDependencies)
    .build();

export { MESSAGING_CONTEXT_ID, baseBootstrap };
